import React, { useState, useEffect } from 'react';
import { listTools, getToolDetails, getToolCategories, getToolTags, getToolStatusCounts } from '../services/toolService';
import { Tool, ToolCategory, ToolPermission, ToolStatus, CategoryCount, TagCount } from '../types/tool';
import ToolCard from '../components/tools/ToolCard';
import ToolDetails from '../components/tools/ToolDetails';
import { useTheme } from '../contexts/ThemeContext';
import { useTintedBackground } from '../hooks/useTintedBackground';
import branding from '../branding.json';
import { 
  FiFilter, FiX, FiSearch, FiInfo, FiGrid, FiList, FiArrowUp, FiArrowDown,
  FiTool, FiPlus, FiSliders
} from 'react-icons/fi';

const ToolExplorer: React.FC = () => {
  const { darkMode } = useTheme();
  const [tools, setTools] = useState<Tool[]>([]);
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [tags, setTags] = useState<TagCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Filter states
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [permissionFilter, setPermissionFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [statusCounts, setStatusCounts] = useState<{status: string, count: number}[]>([]);

  // Get branding colors
  const primaryColor = branding.theme?.primaryColor || '#3B82F6';
  const secondaryColor = branding.theme?.secondaryColor || '#1E40AF';
  
  // Tinted background for header
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? primaryColor : secondaryColor,
    enabled: true,
    opacity: 0.05
  });

  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    applyFilters();
  }, [tools, categoryFilter, permissionFilter, tagFilter, searchQuery, statusFilter, sortDirection]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load each data type separately to handle partial failures
      try {
        const toolsData = await listTools();
        setTools(toolsData);
        setFilteredTools(toolsData);
        
        // Select first tool by default if available
        if (toolsData.length > 0 && !selectedTool) {
          setSelectedTool(toolsData[0]);
        }
      } catch (error) {
        console.error('Error loading tools:', error);
      }
      
      try {
        const categoriesData = await getToolCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
      
      try {
        const tagsData = await getToolTags();
        setTags(tagsData);
      } catch (error) {
        console.error('Error loading tags:', error);
      }
      
      try {
        const statusData = await getToolStatusCounts();
        setStatusCounts(statusData);
      } catch (error) {
        console.error('Error loading status counts:', error);
        // Just set empty status counts
        setStatusCounts([]);
      }
    } catch (error) {
      console.error('Error loading tool data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...tools];
    
    // Apply category filter
    if (categoryFilter) {
      result = result.filter(tool => tool.metadata.category === categoryFilter);
    }
    
    // Apply permission filter
    if (permissionFilter) {
      result = result.filter(tool => tool.metadata.permission_level === permissionFilter);
    }
    
    // Apply tag filters
    if (tagFilter.length > 0) {
      result = result.filter(tool => 
        tagFilter.every(tag => tool.metadata.tags.includes(tag))
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      result = result.filter(tool => tool.metadata.status === statusFilter);
    }
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tool => 
        tool.name.toLowerCase().includes(query) || 
        tool.metadata.documentation.summary.toLowerCase().includes(query) ||
        tool.metadata.documentation.description.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      
      if (sortDirection === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
    
    setFilteredTools(result);
    
    // Update selected tool if it's filtered out
    if (selectedTool && !result.find(t => t.name === selectedTool.name)) {
      setSelectedTool(result.length > 0 ? result[0] : null);
    } else if (!selectedTool && result.length > 0) {
      setSelectedTool(result[0]);
    }
  };

  const [quickToolActive, setQuickToolActive] = useState(false);

  const QUICK_TOOLS = [
    { name: 'generate_mermaid_diagram', label: 'Mermaid', icon: '\u25C7' },
    { name: 'plantuml', label: 'PlantUML', icon: '\u25A3' },
    { name: 'search', label: 'Search', icon: '\u2315' },
    { name: 'mindmap', label: 'Mind Map', icon: '\u25CE' },
    { name: 'geojson', label: 'GeoJSON', icon: '\u2295' },
  ];

  const handleQuickTool = (toolName: string) => {
    const tool = tools.find((t: Tool) => t.name === toolName);
    if (tool) {
      setSelectedTool(tool);
      setQuickToolActive(true);
    }
  };

  const handleToolSelect = (tool: Tool) => {
    setSelectedTool(tool);
    setQuickToolActive(false);
  };
  
  const toggleTag = (tag: string) => {
    if (tagFilter.includes(tag)) {
      setTagFilter(tagFilter.filter(t => t !== tag));
    } else {
      setTagFilter([...tagFilter, tag]);
    }
  };
  
  const clearFilters = () => {
    setCategoryFilter('');
    setPermissionFilter('');
    setTagFilter([]);
    setSearchQuery('');
    setStatusFilter('');
  };
  
  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };
  
  // Get color based on status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'stable':
        return darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800';
      case 'beta':
        return darkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'development':
        return darkMode ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'experimental':
        return darkMode ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-800';
      case 'deprecated':
        return darkMode ? 'bg-orange-800 text-orange-200' : 'bg-orange-100 text-orange-800';
      case 'offline':
        return darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
    }
  };

  // Format version string
  const formatVersion = (version?: any) => {
    if (!version) return 'N/A';
    return `v${version.major}.${version.minor}.${version.patch}`;
  };

  return (
    <div className={`container mx-auto p-4 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header Command Bar */}
      <div
        ref={tintedBg.ref}
        className={`
          relative rounded-xl border shadow-lg mb-6 overflow-hidden
          ${darkMode 
            ? 'border-gray-700/50 shadow-blue-500/10' 
            : 'border-gray-200/50 shadow-blue-500/10'
          }
        `}
        style={tintedBg.style}
      >
        <div 
          className="absolute inset-0 pointer-events-none"
          style={tintedBg.overlayStyle}
        />
        
        {/* Banner */}
        <div className={`
          relative z-10 w-full py-1.5 px-4 border-b
          ${darkMode 
            ? 'bg-gradient-to-r from-blue-900/80 via-indigo-900/80 to-blue-900/80 border-gray-700/50' 
            : 'bg-gradient-to-r from-blue-600/90 via-indigo-600/90 to-blue-600/90 border-gray-200/50'
          }
        `}>
          <div className="flex items-center justify-center">
            <div className={`
              text-xs font-black tracking-[0.3em] uppercase
              ${darkMode ? 'text-blue-200' : 'text-white'}
              drop-shadow-sm
            `}>
              TOOL EXPLORER
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className={`
          relative z-10 px-4 py-3 flex flex-col md:flex-row md:items-center gap-4
          ${darkMode 
            ? 'bg-gradient-to-r from-gray-900/90 via-gray-800/90 to-gray-900/90' 
            : 'bg-gradient-to-r from-white/90 via-gray-50/90 to-white/90'
          }
          backdrop-blur-md
        `}>
          {/* Search */}
          <div className="flex-1 min-w-0">
            <div className={`
              relative flex items-center
              ${darkMode ? 'text-gray-300' : 'text-gray-600'}
            `}>
              <FiSearch className="absolute left-3 w-4 h-4 opacity-50" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools by name, description, or tags..."
                className={`
                  w-full pl-10 pr-10 py-2 rounded-lg text-sm transition-all
                  border focus:outline-none focus:ring-2
                  ${darkMode 
                    ? 'bg-gray-800/80 border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500' 
                    : 'bg-white/80 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-500'
                  }
                `}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`
                    absolute right-3 p-0.5 rounded-full transition-colors
                    ${darkMode 
                      ? 'hover:bg-gray-700 text-gray-400' 
                      : 'hover:bg-gray-200 text-gray-500'
                    }
                  `}
                >
                  <FiX className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          {/* View Toggle & Filters */}
          <div className="flex items-center gap-3">
            <div className={`
              flex items-center p-1 rounded-lg border
              ${darkMode 
                ? 'bg-gray-800/50 border-gray-700' 
                : 'bg-white/50 border-gray-200'
              }
            `}>
              <button
                onClick={() => setViewMode('grid')}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                  ${viewMode === 'grid'
                    ? darkMode
                      ? 'bg-blue-600 text-white shadow-inner'
                      : 'bg-blue-500 text-white shadow-inner'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
                title="Grid view"
              >
                <FiGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                  ${viewMode === 'list'
                    ? darkMode
                      ? 'bg-blue-600 text-white shadow-inner'
                      : 'bg-blue-500 text-white shadow-inner'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
                title="List view"
              >
                <FiList className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
            
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                transition-all duration-200
                ${showFilters
                  ? darkMode
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'bg-blue-500 text-white shadow-inner'
                  : darkMode
                    ? 'bg-gray-800/80 border-gray-600 text-gray-300 hover:bg-gray-700 border'
                    : 'bg-white/80 border-gray-300 text-gray-700 hover:bg-gray-100 border'
                }
              `}
            >
              <FiSliders className="w-4 h-4" />
              <span>Filters</span>
              {(categoryFilter || permissionFilter || tagFilter.length > 0 || statusFilter) && (
                <span className={`
                  ml-1 px-1.5 py-0.5 rounded-full text-xs
                  ${showFilters
                    ? 'bg-white/20'
                    : darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                  }
                `}>
                  {[categoryFilter, permissionFilter, statusFilter].filter(Boolean).length + tagFilter.length}
                </span>
              )}
            </button>
            
            {/* Sort Direction */}
            <button
              onClick={toggleSortDirection}
              className={`
                p-2 rounded-lg border transition-all
                ${darkMode 
                  ? 'bg-gray-800/80 border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'bg-white/80 border-gray-300 text-gray-600 hover:bg-gray-100'
                }
              `}
              title={`Sort ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortDirection === 'asc' ? (
                <FiArrowUp className="w-4 h-4" />
              ) : (
                <FiArrowDown className="w-4 h-4" />
              )}
            </button>
          </div>
          
          {/* Stats */}
          <div className={`
            text-sm whitespace-nowrap
            ${darkMode ? 'text-gray-400' : 'text-gray-500'}
          `}>
            {filteredTools.length} of {tools.length} tools
          </div>
        </div>
      </div>
      
      {/* Active Filters Display */}
      {(categoryFilter || permissionFilter || tagFilter.length > 0 || statusFilter || searchQuery) && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active filters:</span>
          
          {searchQuery && (
            <span className={`
              inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
              ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'}
            `}>
              Search: "{searchQuery.substring(0, 20)}{searchQuery.length > 20 ? '...' : ''}"
              <button onClick={() => setSearchQuery('')} className="hover:text-blue-500">
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {statusFilter && (
            <span className={`
              inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
              ${getStatusColor(statusFilter)}
            `}>
              Status: {statusFilter}
              <button onClick={() => setStatusFilter('')} className="hover:opacity-70">
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {categoryFilter && (
            <span className={`
              inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
              ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'}
            `}>
              Category: {categoryFilter}
              <button onClick={() => setCategoryFilter('')} className="hover:opacity-70">
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {permissionFilter && (
            <span className={`
              inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
              ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'}
            `}>
              Permission: {permissionFilter}
              <button onClick={() => setPermissionFilter('')} className="hover:opacity-70">
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {tagFilter.map(tag => (
            <span 
              key={tag}
              className={`
                inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
                ${darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'}
              `}
            >
              Tag: {tag}
              <button onClick={() => toggleTag(tag)} className="hover:opacity-70">
                <FiX className="w-3 h-3" />
              </button>
            </span>
          ))}
          
          <button
            onClick={clearFilters}
            className={`
              text-xs px-2 py-1 rounded transition-colors
              ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'}
            `}
          >
            Clear all
          </button>
        </div>
      )}
      
      {/* Filters panel */}
      {showFilters && (
        <div className={`
          mb-6 p-4 rounded-xl border shadow-lg
          ${darkMode 
            ? 'bg-gray-800/90 border-gray-700' 
            : 'bg-white/90 border-gray-200'
          }
          backdrop-blur-md
        `}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            {(categoryFilter || permissionFilter || tagFilter.length > 0 || statusFilter) && (
              <button
                onClick={clearFilters}
                className={`
                  text-sm px-3 py-1 rounded-lg transition-colors
                  ${darkMode 
                    ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' 
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }
                `}
              >
                Clear All
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Category filter */}
            <div>
              <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Category
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.name}
                    onClick={() => setCategoryFilter(categoryFilter === cat.name ? '' : cat.name)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm transition-all duration-200
                      ${categoryFilter === cat.name
                        ? darkMode
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                          : 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                        : darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }
                    `}
                  >
                    {cat.name}
                    <span className={`
                      ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                      ${categoryFilter === cat.name
                        ? 'bg-white/20'
                        : darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-600'
                      }
                    `}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Permission filter */}
            <div>
              <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Permission Level
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.values(ToolPermission).map(perm => (
                  <button
                    key={perm}
                    onClick={() => setPermissionFilter(permissionFilter === perm ? '' : perm)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm transition-all duration-200
                      ${permissionFilter === perm
                        ? darkMode
                          ? 'bg-green-600 text-white shadow-lg shadow-green-500/30'
                          : 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                        : darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }
                    `}
                  >
                    {perm}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Status filter */}
            <div>
              <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                {statusCounts.length > 0 ? (
                  statusCounts.map(item => (
                    <button
                      key={item.status}
                      onClick={() => setStatusFilter(statusFilter === item.status ? '' : item.status)}
                      className={`
                        px-3 py-1.5 rounded-lg text-sm transition-all duration-200 flex items-center gap-2
                        ${statusFilter === item.status
                          ? 'ring-2 ring-offset-2 ring-blue-500 ' + getStatusColor(item.status)
                          : getStatusColor(item.status) + ' hover:opacity-80'
                        }
                      `}
                    >
                      {item.status}
                      <span className={`
                        px-1.5 py-0.5 rounded-full text-xs
                        ${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}
                      `}>
                        {item.count}
                      </span>
                    </button>
                  ))
                ) : (
                  Object.values(ToolStatus).map(status => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
                      className={`
                        px-3 py-1.5 rounded-lg text-sm transition-all duration-200
                        ${statusFilter === status
                          ? 'ring-2 ring-offset-2 ring-blue-500 ' + getStatusColor(status)
                          : getStatusColor(status) + ' hover:opacity-80'
                        }
                      `}
                    >
                      {status}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Tags filter */}
          {tags.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <button
                    key={tag.name}
                    onClick={() => toggleTag(tag.name)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm transition-all duration-200
                      ${tagFilter.includes(tag.name)
                        ? darkMode
                          ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30'
                          : 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                        : darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }
                    `}
                  >
                    {tag.name}
                    <span className={`
                      ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                      ${tagFilter.includes(tag.name)
                        ? 'bg-white/20'
                        : darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-600'
                      }
                    `}>
                      {tag.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredTools.length === 0 ? (
        <div className={`
          text-center py-16 rounded-xl border-2 border-dashed
          ${darkMode 
            ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
            : 'bg-gray-100 border-gray-200 text-gray-500'
          }
        `}>
          <FiTool className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold mb-2">
            {searchQuery ? 'No tools match your search' : 'No tools available'}
          </h3>
          <p className="mb-6 max-w-md mx-auto">
            {searchQuery 
              ? 'Try adjusting your search terms or clear the filters to see all tools.'
              : 'No tools are currently registered in the system.'
            }
          </p>
          {searchQuery && (
            <button
              onClick={clearFilters}
              className={`
                px-4 py-2 rounded-lg font-medium
                ${darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                }
              `}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
        {/* Quick Tools */}
        <div className="flex flex-wrap gap-2 mb-4 px-1">
          <span className={`text-xs self-center mr-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Quick test:</span>
          {QUICK_TOOLS.map(qt => (
            <button key={qt.name} onClick={() => handleQuickTool(qt.name)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                darkMode ? 'border-gray-700 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-100 text-gray-600'
              }`}
            >
              <span className="mr-1">{qt.icon}</span>{qt.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-300px)]">
          {/* Tool list */}
          <div className="lg:col-span-1 overflow-hidden flex flex-col">
            {/* Scrollable container for tools */}
            <div className={`
              overflow-y-auto pr-2 flex-1
              ${darkMode ? 'scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900' : 
                        'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'}
            `}>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                  {filteredTools.map(tool => (
                    <ToolCard
                      key={tool.name}
                      tool={tool}
                      onClick={() => handleToolSelect(tool)}
                      isSelected={selectedTool?.name === tool.name}
                      darkMode={darkMode}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTools.map(tool => (
                    <div
                      key={tool.name}
                      onClick={() => handleToolSelect(tool)}
                      className={`
                        p-4 rounded-xl cursor-pointer transition-all duration-200 border-2
                        ${selectedTool?.name === tool.name 
                          ? (darkMode 
                              ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-blue-500 shadow-lg' 
                              : 'bg-gradient-to-r from-white to-gray-50 border-blue-500 shadow-lg') 
                          : (darkMode 
                              ? 'bg-gray-800/50 border-transparent hover:border-gray-600 hover:bg-gray-800' 
                              : 'bg-white border-transparent hover:border-gray-300 hover:shadow-md')
                        }
                      `}
                    >
                      <div className="flex justify-between items-start">
                        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {tool.name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(tool.metadata.status || '')}`}>
                          {tool.metadata.status || 'unknown'}
                        </span>
                      </div>
                      <p className={`text-sm mt-2 line-clamp-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {tool.metadata.documentation.summary}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className={`
                          px-2 py-1 rounded-full text-xs
                          ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}
                        `}>
                          {tool.metadata.category}
                        </span>
                        <span className={`
                          px-2 py-1 rounded-full text-xs
                          ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}
                        `}>
                          {tool.metadata.permission_level}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Tool details */}
          <div className="lg:col-span-2 overflow-hidden flex flex-col">
            <div className={`
              overflow-y-auto flex-1 rounded-xl border shadow-lg
              ${darkMode 
                ? 'bg-gray-800/50 border-gray-700' 
                : 'bg-white/80 border-gray-200'
              }
              backdrop-blur-sm
            `}>
              {selectedTool ? (
                <ToolDetails tool={selectedTool} darkMode={darkMode} initialTab={quickToolActive ? 'tryit' : undefined} />
              ) : (
                <div className={`
                  h-full flex flex-col items-center justify-center p-8 text-center
                  ${darkMode ? 'text-gray-400' : 'text-gray-500'}
                `}>
                  <div className={`
                    w-20 h-20 rounded-full flex items-center justify-center mb-4
                    ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}
                  `}>
                    <FiTool className="w-10 h-10 opacity-50" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">No tool selected</h3>
                  <p>Select a tool from the list to view its details and documentation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default ToolExplorer;
